import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Menu,
  Portal,
  Text,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { ListFilter } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import type { ToastLike } from '@mangovoice/mango-ui'
import { LoadingState } from '@mangovoice/mango-ui'
import type { ApiError, ExtensionInstance, UserRoles } from '@mangovoice/types'
import { ExtensionType } from '@mangovoice/types'

import { ExtensionsApiProvider } from '../api/context'
/**
 * Internal hook to get raw api from context (for bulk operations).
 * Separate from useExtensionsApi to avoid circular import issues in this file.
 */
import { useExtensionsApi as __useExtensionsApiRaw } from '../api/context'
import { EXTENSION_QUERY_KEYS } from '../api/endpoints'
import type { ExtensionsApi } from '../api/extensionsApi'
import { BulkActiveStateDialog } from '../components/BulkActiveStateDialog'
import { ExtensionDeleteDialog } from '../components/ExtensionDeleteDialog'
import { ExtensionsListFiltersSideSheet } from '../components/ExtensionsListFiltersSideSheet'
import type { ExtensionPageSize } from '../components/ExtensionsListToolbar'
import { ExtensionsListToolbar } from '../components/ExtensionsListToolbar'
import {
  ExtensionsTable,
  LinesTable,
  OtherExtensionsTable,
} from '../components/ExtensionsTables'
import {
  MassEditLinesDialog,
  type MassEditLinesFormPayload,
} from '../components/MassEditLinesDialog'
import { SetActiveStateDialog } from '../components/SetActiveStateDialog'
import {
  useDeleteAndReassignLine,
  useDeleteExtension,
  useMassEditLines,
  useSetLineActiveState,
} from '../hooks/useExtensionMutations'
import { useLineFilterOptions, useListExtensions } from '../hooks/useExtensions'
import { ExtensionsI18nProvider, useExtensionsI18n } from '../i18n/provider'
import type { ExtensionsTranslationLike } from '../i18n/types'
import type {
  ExtensionForDisplay,
  LineForDisplay,
} from '../types/extension.types'
import { isInactiveExtension } from '../types/extension.types'
import type { ExtensionPermissionFlags } from '../types/extension.types'
import {
  type ExtensionsListViewMode,
  getInitialOrderingForViewMode,
  LINES_SEARCH_PLACEHOLDER,
  type LineStatusFilterValue,
  OTHER_EXTENSIONS_VIEW_MODES,
  OTHER_VIEW_MODE_TO_EXTENSION_TYPE,
  type OtherExtensionsViewMode,
  resolveExtensionsListViewMode,
  TOAST_DURATION_ERROR,
  TOAST_DURATION_SUCCESS,
} from '../utils/extensions'

const formatErrorCode = (code: string): string => {
  return code
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const TYPE_EDIT_ROLE_KEYS: Record<ExtensionType, string> = {
  [ExtensionType.LINE]: 'can_edit_lines',
  [ExtensionType.RING_GROUP]: 'can_edit_ringgroups',
  [ExtensionType.VIRTUAL_FAX]: 'can_edit_virtualfax',
  [ExtensionType.CONFERENCE_ROOM]: 'can_edit_conferencing',
  [ExtensionType.DIAL_PLAN]: 'can_edit_switchboard',
  [ExtensionType.SHARED]: 'can_edit_sharedvoicemail',
  [ExtensionType.CALL_QUEUE]: 'can_edit_callqueues',
}

const hasTypeEditPermission = (
  userRoles: UserRoles | undefined,
  extensionType: ExtensionType,
): boolean | null => {
  if (!userRoles?.roles?.length) return null
  const key = TYPE_EDIT_ROLE_KEYS[extensionType]
  if (!key) return null
  const loweredKey = key.toLowerCase()
  const normalizedRoles = userRoles.roles.map((role) => ({
    name: role.name?.toLowerCase?.() ?? '',
    type: role.type?.toLowerCase?.() ?? '',
  }))
  const hasGranularRoleSignals = Object.values(TYPE_EDIT_ROLE_KEYS).some(
    (permissionKey) =>
      normalizedRoles.some(
        (role) =>
          role.name.includes(permissionKey) ||
          role.type.includes(permissionKey),
      ),
  )
  if (!hasGranularRoleSignals) {
    return null
  }
  return normalizedRoles.some((role) => {
    return (
      role.name === loweredKey ||
      role.type === loweredKey ||
      role.name.includes(loweredKey) ||
      role.type.includes(loweredKey)
    )
  })
}

const ExtensionsLoadingState: React.FC = () => {
  const { text } = useExtensionsI18n()
  return (
    <LoadingState
      layout="alert"
      title={text('extensions.list.loadingExtensions', 'Loading extensions...')}
    />
  )
}

const ErrorState: React.FC<{ error: ApiError }> = ({ error }) => (
  <Alert.Root status="error" p={4}>
    <Alert.Indicator />
    <Box>
      <Text fontWeight="bold">{formatErrorCode(error.code ?? 'error')}</Text>
      <Text>{error.message}</Text>
    </Box>
  </Alert.Root>
)

const EmptyState: React.FC<{ hasSearch?: boolean }> = ({ hasSearch }) => {
  const { text } = useExtensionsI18n()
  return (
    <Alert.Root status="info" p={4}>
      <Alert.Indicator />
      <Box>
        <Text fontWeight="bold">
          {hasSearch
            ? text('extensions.list.noMatch', 'No extensions match your search')
            : text('extensions.list.noExtensions', 'No extensions found')}
        </Text>
        <Text>
          {hasSearch
            ? text(
                'extensions.list.noMatchHint',
                'Try a different search term.',
              )
            : text(
                'extensions.list.noExtensionsHint',
                'There are no extensions for this PBX.',
              )}
        </Text>
      </Box>
    </Alert.Root>
  )
}

type ExtensionsViewMode = ExtensionsListViewMode

function isOtherViewMode(
  viewMode: ExtensionsViewMode,
): viewMode is OtherExtensionsViewMode {
  return OTHER_EXTENSIONS_VIEW_MODES.some((m) => m.viewMode === viewMode)
}

function getViewModeLabel(
  viewMode: ExtensionsViewMode,
  text: (key: string, fallback: string) => string,
): string {
  if (viewMode === 'all')
    return text('extensions.viewModes.extensions', 'Extensions')
  if (viewMode === 'lines') return text('extensions.viewModes.lines', 'Lines')
  if (viewMode === 'ring-groups')
    return text('extensions.viewModes.ringGroups', 'Ring groups')
  if (viewMode === 'call-queues')
    return text('extensions.viewModes.callQueues', 'Call queues')
  if (viewMode === 'conference-rooms')
    return text('extensions.viewModes.conferenceRooms', 'Conference rooms')
  if (viewMode === 'virtual-fax')
    return text('extensions.viewModes.virtualFax', 'Virtual fax')
  if (viewMode === 'shared-voicemail')
    return text('extensions.viewModes.sharedVoicemail', 'Shared Voicemail')
  return text('extensions.viewModes.extensions', 'Extensions')
}

const VIEW_MODE_TO_TYPE_SLUG: Record<OtherExtensionsViewMode, string> = {
  'ring-groups': 'ring-group',
  'call-queues': 'call-queue',
  'conference-rooms': 'conference-room',
  'virtual-fax': 'virtual-fax',
  'shared-voicemail': 'shared-voicemail',
}

const CREATE_EXTENSION_OPTIONS: {
  typeSlug: string
  menuLabelKey: string
  menuLabelFallback: string
}[] = [
  {
    typeSlug: 'line',
    menuLabelKey: 'extensions.createOptions.addLine',
    menuLabelFallback: 'Add Line',
  },
  {
    typeSlug: 'ring-group',
    menuLabelKey: 'extensions.createOptions.addRingGroup',
    menuLabelFallback: 'Add Ring Group',
  },
  {
    typeSlug: 'call-queue',
    menuLabelKey: 'extensions.createOptions.addCallQueue',
    menuLabelFallback: 'Add Call Queue',
  },
  {
    typeSlug: 'conference-room',
    menuLabelKey: 'extensions.createOptions.addConferenceRoom',
    menuLabelFallback: 'Add Conference Room',
  },
  {
    typeSlug: 'virtual-fax',
    menuLabelKey: 'extensions.createOptions.addVirtualFax',
    menuLabelFallback: 'Add Virtual Fax Machine',
  },
  {
    typeSlug: 'shared-voicemail',
    menuLabelKey: 'extensions.createOptions.addSharedVoicemail',
    menuLabelFallback: 'Add Shared Voicemail Box',
  },
]

function getCreateOptionForViewMode(viewMode: ExtensionsViewMode) {
  if (viewMode === 'all') return null
  if (viewMode === 'lines')
    return CREATE_EXTENSION_OPTIONS.find((o) => o.typeSlug === 'line') ?? null
  const typeSlug = VIEW_MODE_TO_TYPE_SLUG[viewMode]
  return CREATE_EXTENSION_OPTIONS.find((o) => o.typeSlug === typeSlug) ?? null
}

interface CardBodyContentProps {
  extensions: ExtensionInstance[]
  viewMode: ExtensionsViewMode
  isLoading: boolean
  error: ApiError | null
  pbxId: string
  canEditExtension: (ext: ExtensionForDisplay) => boolean
  canDeleteExtension: (ext: ExtensionForDisplay) => boolean
  onDeleteClick: (ext: ExtensionForDisplay) => void
  onToggleActiveClick?: (line: LineForDisplay) => void
  ordering: string
  onOrderingChange: (value: string) => void
  hasSearch: boolean
  selectedLineIds?: Set<number>
  onToggleLine?: (lineId: number) => void
  onSelectAllLines?: (checked: boolean) => void
  getEditRoute: (pbxId: string, extension: ExtensionForDisplay) => string
  grafanaBaseUrl?: string
}

const CardBodyContent: React.FC<CardBodyContentProps> = ({
  extensions,
  viewMode,
  isLoading,
  error,
  pbxId,
  canEditExtension,
  canDeleteExtension,
  onDeleteClick,
  onToggleActiveClick,
  ordering,
  onOrderingChange,
  hasSearch,
  selectedLineIds,
  onToggleLine,
  onSelectAllLines,
  getEditRoute,
  grafanaBaseUrl,
}) => {
  if (isLoading) return <ExtensionsLoadingState />
  if (error) return <ErrorState error={error} />
  if (!extensions.length) return <EmptyState hasSearch={hasSearch} />
  if (viewMode === 'lines') {
    return (
      <LinesTable
        lines={extensions as LineForDisplay[]}
        pbxId={pbxId}
        canEditExtension={canEditExtension}
        canDeleteExtension={canDeleteExtension}
        onDeleteClick={onDeleteClick}
        onToggleActiveClick={onToggleActiveClick}
        ordering={ordering}
        onOrderingChange={onOrderingChange}
        selectedLineIds={selectedLineIds}
        onToggleLine={onToggleLine}
        onSelectAll={onSelectAllLines}
        getEditRoute={getEditRoute}
        grafanaBaseUrl={grafanaBaseUrl}
      />
    )
  }
  if (isOtherViewMode(viewMode)) {
    return (
      <OtherExtensionsTable
        extensions={extensions}
        pbxId={pbxId}
        canEditExtension={canEditExtension}
        canDeleteExtension={canDeleteExtension}
        onDeleteClick={onDeleteClick}
        ordering={ordering}
        onOrderingChange={onOrderingChange}
        getEditRoute={getEditRoute}
      />
    )
  }
  return (
    <ExtensionsTable
      extensions={extensions}
      pbxId={pbxId}
      canEditExtension={canEditExtension}
      canDeleteExtension={canDeleteExtension}
      onDeleteClick={onDeleteClick}
      ordering={ordering}
      onOrderingChange={onOrderingChange}
      getEditRoute={getEditRoute}
    />
  )
}

const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_PAGE_SIZE: ExtensionPageSize = 25
const BULK_CONCURRENCY = 5
const DELETE_DISABLED_TYPES = new Set<ExtensionType>([
  ExtensionType.VIRTUAL_FAX,
  ExtensionType.SHARED,
])

export interface ExtensionsListPageProps {
  api: ExtensionsApi
  pbxId: number
  actionsEnabled: boolean
  isStaff?: boolean
  userRoles?: UserRoles
  toast: ToastLike
  /** Current view query param (from URL search params). */
  viewParam?: string | null
  /** Optional initial list of assigned user IDs to filter Lines by (e.g. from URL). */
  initialAssignedUserFilter?: number[]
  /** Route builder: extension edit path. */
  getEditRoute: (pbxId: string, extension: ExtensionForDisplay) => string
  /** Route builder: new extension path. */
  getNewRoute: (pbxId: string, typeSlug: string) => string
  /** Optional Grafana base URL for call metrics links. */
  grafanaBaseUrl?: string
  /** Optional wrapper component (e.g. PageWrapper with breadcrumbs). */
  wrapper?: React.ComponentType<{ children: React.ReactNode }>
  /** i18next translation function. Pass `t` from `useTranslation()` to enable i18n. */
  t?: ExtensionsTranslationLike
  /** Current locale code (e.g. 'en-US', 'es'). */
  locale?: string
}

const ExtensionsListPageInner: React.FC<ExtensionsListPageProps> = ({
  pbxId,
  actionsEnabled,
  userRoles,
  toast,
  viewParam,
  initialAssignedUserFilter,
  getEditRoute,
  getNewRoute,
  grafanaBaseUrl,
  wrapper: Wrapper,
}) => {
  const { text } = useExtensionsI18n()
  const queryClient = useQueryClient()
  const api = __useExtensionsApiRaw()
  const canEdit = actionsEnabled
  const canDelete = actionsEnabled
  const canMassEditLines = Boolean(userRoles?.is_superuser)
  const isInternalUser = Boolean(userRoles?.is_staff || userRoles?.is_superuser)

  const pbxIdNum = pbxId
  const pbxIdStr = String(pbxId)

  const viewMode: ExtensionsViewMode = useMemo(
    () => resolveExtensionsListViewMode(viewParam),
    [viewParam],
  )

  const pageLabel = getViewModeLabel(viewMode, text)

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [ordering, setOrdering] = useState(() =>
    getInitialOrderingForViewMode(resolveExtensionsListViewMode(viewParam)),
  )
  const [pageSize, setPageSize] = useState<ExtensionPageSize>(DEFAULT_PAGE_SIZE)
  const [currentPage, setCurrentPage] = useState(1)
  const hasInitialAssignedUserFilter =
    initialAssignedUserFilter != null && initialAssignedUserFilter.length > 0
  // Non-staff users cannot see inactive extensions (legacy admin gates the
  // active-state UI behind `is_mango_user`). Pin the status filter to
  // 'active' for them so we never request inactive lines and the filter
  // chip badge does not count this filter for them.
  const initialStatusFilter: LineStatusFilterValue | 'all' = !isInternalUser
    ? 'active'
    : hasInitialAssignedUserFilter
      ? 'all'
      : 'active'
  const [statusFilter, setStatusFilter] = useState<
    LineStatusFilterValue | 'all'
  >(initialStatusFilter)
  const [assignedUserFilter, setAssignedUserFilter] = useState<number[]>(
    initialAssignedUserFilter ?? [],
  )
  const [locationFilter, setLocationFilter] = useState<number[]>([])
  const [pendingStatusFilter, setPendingStatusFilter] = useState<
    LineStatusFilterValue | 'all'
  >(initialStatusFilter)
  const [pendingAssignedUserFilter, setPendingAssignedUserFilter] = useState<
    number[]
  >(initialAssignedUserFilter ?? [])
  const [pendingLocationFilter, setPendingLocationFilter] = useState<number[]>(
    [],
  )
  const [selectedLineIds, setSelectedLineIds] = useState<Set<number>>(new Set())
  const [massEditDialogOpen, setMassEditDialogOpen] = useState(false)
  const [isFiltersSideSheetOpen, setIsFiltersSideSheetOpen] = useState(false)

  const { users: lineUsers, locations: lineLocations } =
    useLineFilterOptions(pbxIdNum)

  const activeFilterCount = useMemo(() => {
    if (viewMode !== 'lines') return 0
    let count = 0
    // Status is always an explicit filter unless user selects "All". Skip it
    // for non-staff users since the status filter is hidden from them.
    if (isInternalUser && statusFilter !== 'all') count++
    if (assignedUserFilter.length > 0) count++
    if (locationFilter.length > 0) count++
    return count
  }, [
    viewMode,
    isInternalUser,
    statusFilter,
    assignedUserFilter,
    locationFilter,
  ])

  const openFiltersSideSheet = () => {
    setPendingStatusFilter(statusFilter)
    setPendingAssignedUserFilter(assignedUserFilter)
    setPendingLocationFilter(locationFilter)
    setIsFiltersSideSheetOpen(true)
  }

  const applyPendingFilters = () => {
    setStatusFilter(pendingStatusFilter)
    setAssignedUserFilter(pendingAssignedUserFilter)
    setLocationFilter(pendingLocationFilter)
    setIsFiltersSideSheetOpen(false)
  }

  const clearPendingFilters = () => {
    // Non-staff users can't see inactive extensions, so "clear" for them
    // means resetting status to 'active' rather than 'all'.
    const clearedStatus: LineStatusFilterValue | 'all' = isInternalUser
      ? 'all'
      : 'active'
    const clearedAssignedUsers: number[] = []
    const clearedLocations: number[] = []
    setPendingStatusFilter(clearedStatus)
    setPendingAssignedUserFilter(clearedAssignedUsers)
    setPendingLocationFilter(clearedLocations)
    setStatusFilter(clearedStatus)
    setAssignedUserFilter(clearedAssignedUsers)
    setLocationFilter(clearedLocations)
    setIsFiltersSideSheetOpen(false)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, ordering])
  useEffect(() => {
    setCurrentPage(1)
  }, [pageSize])
  useEffect(() => {
    setCurrentPage(1)
  }, [viewMode])
  useEffect(() => {
    setOrdering(getInitialOrderingForViewMode(viewMode))
  }, [viewMode])
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, assignedUserFilter, locationFilter])
  useEffect(() => {
    if (viewMode !== 'lines') setSelectedLineIds(new Set())
  }, [viewMode])
  useEffect(() => {
    if (!canMassEditLines) setSelectedLineIds(new Set())
  }, [canMassEditLines])

  const fetchAll = pageSize === 'All'
  const limit = fetchAll ? 250 : (pageSize as number)
  const offset = fetchAll ? 0 : (currentPage - 1) * (pageSize as number)

  const listType =
    viewMode === 'lines'
      ? ExtensionType.LINE
      : isOtherViewMode(viewMode)
        ? OTHER_VIEW_MODE_TO_EXTENSION_TYPE[viewMode]
        : undefined

  const { extensions, count, isLoading, error } = useListExtensions({
    pbxId: pbxIdNum,
    listParams: {
      search: debouncedSearch.trim() || undefined,
      ordering,
      limit,
      offset,
      fetchAll,
      type: listType,
      statusFilter:
        viewMode === 'lines' && statusFilter && statusFilter !== 'all'
          ? (statusFilter as LineStatusFilterValue)
          : undefined,
      assignedUserFilter:
        viewMode === 'lines' && assignedUserFilter.length > 0
          ? assignedUserFilter
          : undefined,
      locationFilter:
        viewMode === 'lines' && locationFilter.length > 0
          ? locationFilter
          : undefined,
    },
  })

  const visibleExtensions = useMemo(
    () =>
      extensions.filter(
        (extension) =>
          (extension.type as unknown as string) !== 'ExtensionSystem',
      ),
    [extensions],
  )

  const deleteMutation = useDeleteExtension(pbxIdNum)
  const deleteAndReassignMutation = useDeleteAndReassignLine(pbxIdNum)
  const massEditMutation = useMassEditLines(pbxIdNum)

  const canEditExtension = useMemo(
    () => (extension: ExtensionForDisplay) => {
      if (!canEdit) return false
      const permissions = extension as ExtensionPermissionFlags
      const flag =
        permissions.permissions?.can_edit ??
        permissions.permissions?.can_update ??
        permissions.can_edit ??
        permissions.can_update
      if (flag != null) return flag
      const roleGranted = hasTypeEditPermission(
        userRoles,
        extension.type as ExtensionType,
      )
      if (roleGranted != null) return roleGranted
      return true
    },
    [canEdit, userRoles],
  )

  const canDeleteExtension = useMemo(
    () => (extension: ExtensionForDisplay) => {
      if (DELETE_DISABLED_TYPES.has(extension.type as ExtensionType))
        return false
      // Legacy Admin only allows deleting line extensions for internal users.
      if (extension.type === ExtensionType.LINE && !isInternalUser) return false
      if (!canDelete) return false
      const permissions = extension as ExtensionPermissionFlags
      const flag = permissions.permissions?.can_delete ?? permissions.can_delete
      if (flag != null) return flag
      const roleGranted = hasTypeEditPermission(
        userRoles,
        extension.type as ExtensionType,
      )
      if (roleGranted != null) return roleGranted
      return true
    },
    [canDelete, isInternalUser, userRoles],
  )

  const [extensionToDelete, setExtensionToDelete] =
    useState<ExtensionForDisplay | null>(null)

  const handleDeleteClick = (ext: ExtensionForDisplay) => {
    setExtensionToDelete(ext)
  }

  const handleDeleteConfirm = async (reassignToExtensionId?: number | null) => {
    if (!extensionToDelete) return
    try {
      if (extensionToDelete.type === ExtensionType.LINE) {
        if (reassignToExtensionId != null) {
          await deleteAndReassignMutation.mutateAsync({
            extensionId: extensionToDelete.id,
            reassignToExtensionId,
          })
        } else {
          await deleteMutation.mutateAsync(extensionToDelete.id)
        }
      } else {
        await deleteMutation.mutateAsync(extensionToDelete.id)
      }
      setExtensionToDelete(null)
      toast.create({
        title: text('extensions.toast.success', 'Success'),
        description: text(
          'extensions.toast.deleteSuccess',
          'Extension deleted successfully.',
        ),
        type: 'success',
        duration: TOAST_DURATION_SUCCESS,
        closable: true,
      })
    } catch {
      toast.create({
        title: text('extensions.toast.deleteFailed', 'Delete failed'),
        description: text(
          'extensions.toast.deleteFailedDescription',
          'Failed to delete extension. Please try again.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
    }
  }

  const displayNameToDelete =
    extensionToDelete?.name ||
    extensionToDelete?.number ||
    (extensionToDelete
      ? text('extensions.row.extensionFallback', 'Extension {{id}}', {
          id: extensionToDelete.id,
        })
      : '')

  const [lineToToggleActive, setLineToToggleActive] =
    useState<LineForDisplay | null>(null)
  const [bulkActiveTarget, setBulkActiveTarget] = useState<boolean | null>(null)
  const [bulkActivePending, setBulkActivePending] = useState(false)

  const singleActiveMutation = useSetLineActiveState(
    lineToToggleActive?.id,
    pbxIdNum,
  )

  const handleToggleActiveClick = (line: LineForDisplay) => {
    setLineToToggleActive(line)
  }

  const handleToggleActiveConfirm = async () => {
    if (!lineToToggleActive) return
    const targetActive = isInactiveExtension(lineToToggleActive)
    try {
      await singleActiveMutation.mutateAsync(targetActive)
      setLineToToggleActive(null)
      toast.create({
        title: text('extensions.toast.success', 'Success'),
        description: text(
          'extensions.toast.activeStateSuccess',
          'Line set to {{state}}.',
          {
            state: targetActive
              ? text('extensions.filters.active', 'active')
              : text('extensions.filters.inactive', 'inactive'),
          },
        ),
        type: 'success',
        duration: TOAST_DURATION_SUCCESS,
        closable: true,
      })
    } catch {
      toast.create({
        title: text('extensions.toast.failed', 'Failed'),
        description: text(
          'extensions.toast.activeStateFailed',
          'Failed to update line active state.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
    }
  }

  const handleBulkActivateAll = () => setBulkActiveTarget(true)
  const handleBulkDeactivateAll = () => setBulkActiveTarget(false)

  const handleBulkActiveConfirm = async () => {
    if (bulkActiveTarget == null || !pbxIdNum) return
    setBulkActivePending(true)
    const linesToUpdate = visibleExtensions.filter((ext) => {
      if (ext.type !== ExtensionType.LINE) return false
      const inactive = isInactiveExtension(ext as ExtensionForDisplay)
      return bulkActiveTarget ? inactive : !inactive
    })

    let succeeded = 0
    let failed = 0
    for (let i = 0; i < linesToUpdate.length; i += BULK_CONCURRENCY) {
      const batch = linesToUpdate.slice(i, i + BULK_CONCURRENCY)
      const batchResults = await Promise.allSettled(
        batch.map((ext) =>
          api.setLineActiveState(pbxIdNum, ext.id, bulkActiveTarget),
        ),
      )
      for (const result of batchResults) {
        if (result.status === 'fulfilled') succeeded++
        else failed++
      }
    }

    queryClient.invalidateQueries({
      queryKey: [...EXTENSION_QUERY_KEYS.lists(), pbxIdNum],
    })
    setBulkActiveTarget(null)
    setBulkActivePending(false)

    const label = bulkActiveTarget
      ? text('extensions.filters.active', 'active')
      : text('extensions.filters.inactive', 'inactive')
    if (failed === 0) {
      toast.create({
        title: text('extensions.toast.success', 'Success'),
        description: text(
          'extensions.toast.bulkSuccess',
          '{{count}} line{{suffix}} set to {{state}}.',
          {
            count: succeeded,
            suffix: succeeded !== 1 ? 's' : '',
            state: label,
          },
        ),
        type: 'success',
        duration: TOAST_DURATION_SUCCESS,
        closable: true,
      })
    } else {
      toast.create({
        title: text('extensions.toast.partialFailure', 'Partial failure'),
        description: text(
          'extensions.toast.partialFailureDescription',
          '{{succeeded}} succeeded, {{failed}} failed. Some lines may not have been updated.',
          { succeeded, failed },
        ),
        type: 'warning',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
    }
  }

  const handleToggleLine = (lineId: number) => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev)
      if (next.has(lineId)) next.delete(lineId)
      else next.add(lineId)
      return next
    })
  }

  const handleSelectAllLines = (checked: boolean) => {
    setSelectedLineIds(
      checked ? new Set(extensions.map((e) => e.id)) : new Set(),
    )
  }

  const handleMassEditConfirm = async (payload: MassEditLinesFormPayload) => {
    if (!canMassEditLines) {
      toast.create({
        title: text('extensions.toast.massEditFailed', 'Mass edit failed'),
        description: text(
          'extensions.toast.massEditPermissionDenied',
          'You do not have permission to mass edit lines.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    try {
      const result = await massEditMutation.mutateAsync({
        extension_ids: Array.from(selectedLineIds),
        ...payload,
      })
      const updated = result?.updated ?? 0
      toast.create({
        title: text('extensions.toast.success', 'Success'),
        description: text(
          'extensions.toast.massEditSuccess',
          'Updated {{count}} line{{suffix}}.',
          { count: updated, suffix: updated !== 1 ? 's' : '' },
        ),
        type: 'success',
        duration: TOAST_DURATION_SUCCESS,
        closable: true,
      })
      setMassEditDialogOpen(false)
    } catch {
      toast.create({
        title: text('extensions.toast.massEditFailed', 'Mass edit failed'),
        description: text(
          'extensions.toast.massEditFailedDescription',
          'Failed to update selected lines. Try again.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
    }
  }

  const handleCloseMassEditDialog = () => {
    if (!massEditMutation.isPending) {
      setMassEditDialogOpen(false)
    }
  }

  const content = (
    <>
      <Card.Root h="full" minH={0} display="flex" flexDirection="column">
        <Card.Header p={6} pb={4}>
          <Flex
            align={{ base: 'stretch', md: 'center' }}
            justify="space-between"
            gap={4}
            direction={{ base: 'column', md: 'row' }}
          >
            <Box flexShrink={0}>
              <Heading size="lg" color="fg.default">
                {pageLabel}
              </Heading>
            </Box>
            <HStack
              gap={2}
              flex={1}
              flexWrap="wrap"
              alignItems="center"
              justify={{ base: 'flex-start', md: 'flex-end' }}
            >
              <Flex align="center" gap={0}>
                <Box w={{ base: 'full', md: '280px' }} position="relative">
                  <Icon
                    boxSize={4}
                    color="fg.muted"
                    position="absolute"
                    left={3}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={1}
                    pointerEvents="none"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                  </Icon>
                  <Input
                    placeholder={
                      viewMode === 'lines'
                        ? text(
                            'extensions.list.linesSearchPlaceholder',
                            LINES_SEARCH_PLACEHOLDER,
                          )
                        : text(
                            'extensions.list.searchPlaceholder',
                            'Search by name or number',
                          )
                    }
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    size="sm"
                    pl={10}
                    bg="bg.subtle"
                    borderColor="border.default"
                    borderRightRadius={viewMode === 'lines' ? 0 : undefined}
                    _placeholder={{ color: 'fg.subtle' }}
                    _hover={{ borderColor: 'border.emphasized' }}
                    _focus={{
                      borderColor: 'brand.solid',
                      boxShadow: '0 0 0 1px var(--chakra-colors-brand-solid)',
                    }}
                    aria-label={text(
                      'extensions.pagination.searchExtensions',
                      'Search extensions',
                    )}
                  />
                </Box>
                {viewMode === 'lines' && (
                  <Box position="relative">
                    <IconButton
                      aria-label={text(
                        'extensions.pagination.toggleFilters',
                        'Toggle filters',
                      )}
                      size="sm"
                      variant={activeFilterCount > 0 ? 'solid' : 'outline'}
                      colorPalette={activeFilterCount > 0 ? 'brand' : undefined}
                      onClick={openFiltersSideSheet}
                      borderLeftRadius={0}
                      borderLeftWidth={0}
                    >
                      <Icon
                        as={ListFilter}
                        boxSize={4}
                        color={
                          activeFilterCount > 0
                            ? 'colorPalette.contrast'
                            : 'fg.default'
                        }
                      />
                    </IconButton>
                    {activeFilterCount > 0 && (
                      <Badge
                        position="absolute"
                        top="-1"
                        right="-1"
                        size="xs"
                        colorPalette="info"
                        borderRadius="full"
                        minW={4}
                        h={4}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="2xs"
                      >
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Box>
                )}
              </Flex>

              {viewMode === 'lines' && canEdit && isInternalUser && (
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button size="sm" variant="outline" borderRadius="4px">
                      {text('extensions.list.actions', 'Actions')}
                      <Icon boxSize={4} ml={1}>
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            d="m6 9 6 6 6-6"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Icon>
                    </Button>
                  </Menu.Trigger>
                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content minW="220px">
                        <Menu.Item
                          value="activate-all"
                          onClick={handleBulkActivateAll}
                        >
                          {text('extensions.list.activateAll', 'Activate All')}
                        </Menu.Item>
                        <Menu.Item
                          value="deactivate-all"
                          onClick={handleBulkDeactivateAll}
                        >
                          {text(
                            'extensions.list.deactivateAll',
                            'Deactivate All',
                          )}
                        </Menu.Item>
                        {canMassEditLines && (
                          <Menu.Item
                            value="mass-edit"
                            onClick={() => setMassEditDialogOpen(true)}
                            disabled={selectedLineIds.size === 0}
                          >
                            {text('extensions.list.massEdit', 'Mass edit')}
                          </Menu.Item>
                        )}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>
              )}

              {canEdit &&
                (() => {
                  const createOption = getCreateOptionForViewMode(viewMode)
                  if (createOption) {
                    if (createOption.typeSlug === 'line' && !isInternalUser)
                      return null
                    return (
                      <Button
                        asChild
                        alignSelf={{ base: 'stretch', md: 'flex-start' }}
                        colorPalette="brand"
                        color="brand.contrast"
                        size="sm"
                        borderRadius="4px"
                      >
                        <Link to={getNewRoute(pbxIdStr, createOption.typeSlug)}>
                          {text(
                            createOption.menuLabelKey,
                            createOption.menuLabelFallback,
                          )}
                        </Link>
                      </Button>
                    )
                  }
                  const createOptions = isInternalUser
                    ? CREATE_EXTENSION_OPTIONS
                    : CREATE_EXTENSION_OPTIONS.filter(
                        (o) => o.typeSlug !== 'line',
                      )
                  if (createOptions.length === 0) return null
                  return (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          alignSelf={{ base: 'stretch', md: 'flex-start' }}
                          colorPalette="brand"
                          color="brand.contrast"
                          size="sm"
                          borderRadius="4px"
                        >
                          {text('extensions.createOptions.add', 'Add')}
                          <Icon boxSize={4} ml={1} color="brand.contrast">
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                            >
                              <path
                                d="m6 9 6 6 6-6"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </Icon>
                        </Button>
                      </Menu.Trigger>
                      <Portal>
                        <Menu.Positioner>
                          <Menu.Content minW="220px">
                            {createOptions.map((option) => (
                              <Menu.Item
                                key={option.typeSlug}
                                value={`add-${option.typeSlug}`}
                                asChild
                              >
                                <Link
                                  to={getNewRoute(pbxIdStr, option.typeSlug)}
                                >
                                  {text(
                                    option.menuLabelKey,
                                    option.menuLabelFallback,
                                  )}
                                </Link>
                              </Menu.Item>
                            ))}
                          </Menu.Content>
                        </Menu.Positioner>
                      </Portal>
                    </Menu.Root>
                  )
                })()}
            </HStack>
          </Flex>
        </Card.Header>
        <Card.Body
          p={6}
          pt={2}
          flex={1}
          minH={0}
          overflowY="auto"
          display="flex"
          flexDirection="column"
        >
          <>
            {viewMode === 'lines' &&
              canMassEditLines &&
              selectedLineIds.size > 0 && (
                <Text fontSize="sm" color="fg.subtle" mb={3}>
                  {text(
                    'extensions.list.linesSelected',
                    '{{count}} line{{suffix}} selected',
                    {
                      count: selectedLineIds.size,
                      suffix: selectedLineIds.size !== 1 ? 's' : '',
                    },
                  )}
                </Text>
              )}
            <CardBodyContent
              extensions={visibleExtensions}
              viewMode={viewMode}
              isLoading={isLoading}
              error={error}
              pbxId={pbxIdStr}
              canEditExtension={canEditExtension}
              canDeleteExtension={canDeleteExtension}
              onDeleteClick={handleDeleteClick}
              onToggleActiveClick={
                canEdit && isInternalUser ? handleToggleActiveClick : undefined
              }
              hasSearch={debouncedSearch.length > 0}
              selectedLineIds={canMassEditLines ? selectedLineIds : undefined}
              onToggleLine={canMassEditLines ? handleToggleLine : undefined}
              onSelectAllLines={
                canMassEditLines ? handleSelectAllLines : undefined
              }
              ordering={ordering}
              onOrderingChange={setOrdering}
              getEditRoute={getEditRoute}
              grafanaBaseUrl={grafanaBaseUrl}
            />
            <ExtensionsListToolbar
              searchValue={searchInput}
              onSearchChange={setSearchInput}
              searchPlaceholder={
                viewMode === 'lines'
                  ? text(
                      'extensions.list.linesSearchPlaceholder',
                      LINES_SEARCH_PLACEHOLDER,
                    )
                  : text(
                      'extensions.list.searchPlaceholder',
                      'Search by name or number',
                    )
              }
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              currentPage={currentPage}
              totalCount={count}
              onPageChange={setCurrentPage}
              isLoading={isLoading}
              showSearch={false}
              showPagingControls
            />
          </>
        </Card.Body>
      </Card.Root>

      <ExtensionDeleteDialog
        isOpen={extensionToDelete != null}
        onClose={() => setExtensionToDelete(null)}
        displayName={displayNameToDelete}
        onConfirm={handleDeleteConfirm}
        isDeleting={
          deleteMutation.isPending || deleteAndReassignMutation.isPending
        }
        extensionType={extensionToDelete?.type}
        otherLines={
          extensionToDelete?.type === ExtensionType.LINE
            ? visibleExtensions
                .filter(
                  (e) =>
                    e.type === ExtensionType.LINE &&
                    e.id !== extensionToDelete.id,
                )
                .map((e) => ({
                  id: e.id,
                  name:
                    e.name ??
                    text(
                      'extensions.deleteDialog.unnamed',
                      'Unnamed Extension',
                    ),
                  number: e.number,
                }))
            : undefined
        }
        allowReassign={Boolean(userRoles?.is_superuser)}
      />
      {viewMode === 'lines' && (
        <ExtensionsListFiltersSideSheet
          isOpen={isFiltersSideSheetOpen}
          value={{
            statusFilter: pendingStatusFilter,
            assignedUserFilter: pendingAssignedUserFilter,
            locationFilter: pendingLocationFilter,
          }}
          users={lineUsers}
          locations={lineLocations}
          showStatusFilter={isInternalUser}
          onChange={(next) => {
            setPendingStatusFilter(next.statusFilter)
            setPendingAssignedUserFilter(next.assignedUserFilter)
            setPendingLocationFilter(next.locationFilter)
          }}
          onClear={clearPendingFilters}
          onCancel={() => setIsFiltersSideSheetOpen(false)}
          onApply={applyPendingFilters}
        />
      )}

      <MassEditLinesDialog
        isOpen={canMassEditLines && massEditDialogOpen}
        onClose={handleCloseMassEditDialog}
        selectedCount={selectedLineIds.size}
        pbxId={pbxIdNum}
        onConfirm={handleMassEditConfirm}
        isSubmitting={massEditMutation.isPending}
      />

      <SetActiveStateDialog
        isOpen={lineToToggleActive != null}
        onClose={() => setLineToToggleActive(null)}
        targetActive={
          lineToToggleActive ? isInactiveExtension(lineToToggleActive) : false
        }
        displayName={
          lineToToggleActive?.name ||
          lineToToggleActive?.number ||
          (lineToToggleActive
            ? text('extensions.row.extensionFallback', 'Extension {{id}}', {
                id: lineToToggleActive.id,
              })
            : '')
        }
        onConfirm={handleToggleActiveConfirm}
        isPending={singleActiveMutation.isPending}
      />

      <BulkActiveStateDialog
        isOpen={bulkActiveTarget != null}
        onClose={() => setBulkActiveTarget(null)}
        targetActive={bulkActiveTarget ?? false}
        onConfirm={handleBulkActiveConfirm}
        isPending={bulkActivePending}
      />
    </>
  )

  if (Wrapper) {
    return <Wrapper>{content}</Wrapper>
  }
  return content
}

/**
 * ExtensionsListPage wraps its children in ExtensionsApiProvider and ExtensionsI18nProvider.
 */
export const ExtensionsListPage: React.FC<ExtensionsListPageProps> = (
  props,
) => {
  return (
    <ExtensionsApiProvider api={props.api}>
      <ExtensionsI18nProvider t={props.t} locale={props.locale}>
        <ExtensionsListPageInner {...props} />
      </ExtensionsI18nProvider>
    </ExtensionsApiProvider>
  )
}
